import React, { useState, Fragment, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ChevronDownIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Layers, ListChecks } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/helpers';
import { DifficultyMeterSelector, TimeMeterSelector } from '@/components/common/MeterSelectors';
import { PrioritySelector } from '@/components/tracks/Selectors';
import { trackIcons, getTrackIcon, trackColors } from '@/components/tracks/constants';
import type { DifficultyLevel, PriorityLevel } from '@/components/tracks/types';

// ============================================================================
// Types
// ============================================================================
export interface AICard {
  id: string;
  title: string;
  description?: string;
  cardType?: 'watch' | 'read' | 'practice' | 'build' | 'revise' | 'notes' | 'flashcard' | 'custom';
  difficulty?: DifficultyLevel;
  priority?: PriorityLevel;
  estimatedMinutes?: number;
}

export interface AIPattern {
  id: string;
  name: string;
  description?: string;
  difficulty?: DifficultyLevel;
  priority?: PriorityLevel;
  estimatedMinutes?: number;
  cards: AICard[];
}

export interface AILane {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  patterns: AIPattern[];
}

export interface AIMap {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  lanes: AILane[];
}

export interface AITrackData {
  trackName: string;
  trackColor: string;
  trackIcon: string;
  trackType: 'hierarchical' | 'linear';
  category?: string;
  description?: string;
  maps?: AIMap[];
  cards?: AICard[]; // For linear tracks
}

interface AITrackPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: AITrackData;
  onConfirm: (data: AITrackData) => void;
  isCreating?: boolean;
}

// ============================================================================
// Helper functions
// ============================================================================
const generateId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const formatTime = (mins?: number) => {
  if (!mins || mins <= 0) return null;
  if (mins >= 10080) return `${Math.round(mins / 10080)}w`;
  if (mins >= 1440) return `${Math.round(mins / 1440)}d`;
  if (mins >= 60) return `${Math.round(mins / 60)}h`;
  return `${mins}m`;
};

// ============================================================================
// Google-style Checkbox
// ============================================================================
const Checkbox = ({ checked = false, indeterminate = false, onClick }: {
  checked?: boolean;
  indeterminate?: boolean;
  onClick?: () => void;
}) => (
  <button
    onClick={onClick}
    className={cn(
      'w-[18px] h-[18px] rounded-sm border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer',
      (checked || indeterminate)
        ? 'bg-[#1a73e8] border-[#1a73e8]'
        : 'border-[#dadce0] dark:border-gray-600 bg-transparent hover:border-[#1a73e8]'
    )}
  >
    {indeterminate && <div className="w-2 h-0.5 bg-white rounded-full" />}
    {checked && !indeterminate && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} />}
  </button>
);

// ============================================================================
// Editable Name Component
// ============================================================================
const EditableName = ({
  value,
  onChange,
  placeholder = 'Enter name...',
  className = ''
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSubmit = () => {
    onChange(editValue);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        autoFocus
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') {
            setEditValue(value);
            setIsEditing(false);
          }
        }}
        className={cn(
          'bg-transparent border-b border-[#1a73e8] outline-none text-[#202124] dark:text-gray-100',
          className
        )}
        placeholder={placeholder}
      />
    );
  }

  return (
    <span
      onClick={() => {
        setEditValue(value);
        setIsEditing(true);
      }}
      className={cn('cursor-text hover:bg-gray-100 dark:hover:bg-gray-700 px-1 -mx-1 rounded', className)}
    >
      {value || placeholder}
    </span>
  );
};

// ============================================================================
// Property Icons Row (Difficulty, Priority, Time)
// ============================================================================
const PropertyIcons = ({
  difficulty,
  priority,
  estimatedMinutes,
  onDifficultyChange,
  onPriorityChange,
  onTimeChange,
  size = 'xs',
}: {
  difficulty?: DifficultyLevel;
  priority?: PriorityLevel;
  estimatedMinutes?: number;
  onDifficultyChange: (d: DifficultyLevel | undefined) => void;
  onPriorityChange: (p: PriorityLevel | undefined) => void;
  onTimeChange: (m: number | undefined) => void;
  size?: 'xs' | 'sm';
}) => (
  <div className="flex items-center gap-1 shrink-0">
    <TimeMeterSelector value={estimatedMinutes} onChange={onTimeChange} size={size} />
    <DifficultyMeterSelector value={difficulty} onChange={onDifficultyChange} size={size} />
    <PrioritySelector value={priority} onChange={onPriorityChange} size={size} />
  </div>
);

// ============================================================================
// Action Buttons (Edit, Delete)
// ============================================================================
const ActionButtons = ({
  onDelete,
  showOnHover = true,
}: {
  onDelete: () => void;
  showOnHover?: boolean;
}) => (
  <div className={cn(
    'flex items-center gap-1',
    showOnHover && 'lg:opacity-0 lg:group-hover:opacity-100 transition-opacity'
  )}>
    <button
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors"
    >
      <TrashIcon className="w-3.5 h-3.5" />
    </button>
  </div>
);

// ============================================================================
// Component Props Types
// ============================================================================
interface CardItemProps {
  card: AICard;
  onUpdate: (card: AICard) => void;
  onDelete: () => void;
}

interface PatternItemProps {
  pattern: AIPattern;
  onUpdate: (pattern: AIPattern) => void;
  onDelete: () => void;
  onAddCard: () => void;
}

interface LaneItemProps {
  lane: AILane;
  onUpdate: (lane: AILane) => void;
  onDelete: () => void;
  onAddPattern: () => void;
}

interface MapItemProps {
  map: AIMap;
  onUpdate: (map: AIMap) => void;
  onDelete: () => void;
  onAddLane: () => void;
}

// ============================================================================
// Card Item Component
// ============================================================================
const CardItem = React.forwardRef(function CardItem({ card, onUpdate, onDelete }: CardItemProps, ref: React.ForwardedRef<HTMLDivElement>) { return (
  <motion.div
    ref={ref}
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -10 }}
    className="group flex items-center gap-2 py-1.5 pl-2 pr-3 rounded hover:bg-[#f1f3f4] dark:hover:bg-gray-800 transition-colors"
  >
    <Checkbox checked={false} />
    <EditableName
      value={card.title}
      onChange={(title) => onUpdate({ ...card, title })}
      className="flex-1 text-[13px] text-[#3c4043] dark:text-gray-300 truncate"
      placeholder="Enter card title..."
    />
    <PropertyIcons
      difficulty={card.difficulty}
      priority={card.priority}
      estimatedMinutes={card.estimatedMinutes}
      onDifficultyChange={(d) => onUpdate({ ...card, difficulty: d })}
      onPriorityChange={(p) => onUpdate({ ...card, priority: p })}
      onTimeChange={(m) => onUpdate({ ...card, estimatedMinutes: m })}
    />
    <ActionButtons onDelete={onDelete} />
  </motion.div>
); });
CardItem.displayName = 'CardItem';

// ============================================================================
// Pattern Item Component (Level 3)
// ============================================================================
const PatternItem = React.forwardRef(function PatternItem({ pattern, onUpdate, onDelete, onAddCard }: PatternItemProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasCards = pattern.cards && pattern.cards.length > 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="border border-[#e8eaed] dark:border-gray-700 rounded-lg overflow-hidden"
    >
      {/* Pattern Header */}
      <div className="group flex items-center gap-2 py-2 pl-3 pr-3 hover:bg-[#f1f3f4] dark:hover:bg-gray-800 transition-colors">
        <Checkbox checked={false} />

        {hasCards ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-0.5 hover:bg-[#e8eaed] dark:hover:bg-gray-700 rounded"
          >
            <ChevronDownIcon
              className={cn(
                'w-4 h-4 text-[#5f6368] transition-transform',
                !isExpanded && '-rotate-90'
              )}
            />
          </button>
        ) : (
          <div className="w-5" />
        )}

        <EditableName
          value={pattern.name}
          onChange={(name) => onUpdate({ ...pattern, name })}
          className="flex-1 text-sm text-[#202124] dark:text-gray-100 truncate font-medium"
          placeholder="Enter pattern name..."
        />

        <PropertyIcons
          difficulty={pattern.difficulty}
          priority={pattern.priority}
          estimatedMinutes={pattern.estimatedMinutes}
          onDifficultyChange={(d) => onUpdate({ ...pattern, difficulty: d })}
          onPriorityChange={(p) => onUpdate({ ...pattern, priority: p })}
          onTimeChange={(m) => onUpdate({ ...pattern, estimatedMinutes: m })}
          size="xs"
        />

        {hasCards && (
          <span className="text-[11px] text-[#5f6368] dark:text-gray-400">
            {pattern.cards.length}
          </span>
        )}

        <ActionButtons onDelete={onDelete} />
      </div>

      {/* Cards */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-[#e8eaed] dark:border-gray-700"
          >
            <div className="ml-10 py-1 space-y-0.5">
              <AnimatePresence mode="popLayout">
                {pattern.cards.map((card, idx) => (
                  <CardItem
                    key={card.id}
                    card={card}
                    onUpdate={(updated) => {
                      const newCards = [...pattern.cards];
                      newCards[idx] = updated;
                      onUpdate({ ...pattern, cards: newCards });
                    }}
                    onDelete={() => {
                      const newCards = pattern.cards.filter((_, i) => i !== idx);
                      onUpdate({ ...pattern, cards: newCards });
                    }}
                  />
                ))}
              </AnimatePresence>

              {/* Add Card Button */}
              <button
                onClick={onAddCard}
                className="flex items-center gap-2 py-1.5 pl-2 pr-3 w-full text-left text-[11px] text-[#1a73e8] hover:bg-[#e8f0fe] dark:hover:bg-blue-900/30 rounded transition-colors"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add card
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});
PatternItem.displayName = 'PatternItem';

// ============================================================================
// Lane Item Component (Level 2)
// ============================================================================
const LaneItem = React.forwardRef(function LaneItem({ lane, onUpdate, onDelete, onAddPattern }: LaneItemProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div ref={ref} className="border border-[#dadce0] dark:border-gray-600 rounded-lg overflow-hidden bg-[#f8f9fa] dark:bg-gray-800/50">
      {/* Lane Header */}
      <div className="group flex items-center gap-2 py-2.5 pl-3 pr-3 bg-white dark:bg-gray-800 border-b border-[#e8eaed] dark:border-gray-700">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-0.5 hover:bg-[#e8eaed] dark:hover:bg-gray-700 rounded"
        >
          <ChevronDownIcon
            className={cn(
              'w-4 h-4 text-[#5f6368] transition-transform',
              !isExpanded && '-rotate-90'
            )}
          />
        </button>

        <EditableName
          value={lane.name}
          onChange={(name) => onUpdate({ ...lane, name })}
          className="flex-1 text-sm text-[#202124] dark:text-gray-100 font-semibold"
          placeholder="Enter lane name..."
        />

        <span className="text-[11px] text-[#5f6368] dark:text-gray-400">
          {lane.patterns.length} patterns
        </span>

        <ActionButtons onDelete={onDelete} />
      </div>

      {/* Patterns */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-2 space-y-2"
          >
            <AnimatePresence mode="popLayout">
              {lane.patterns.map((pattern, idx) => (
                <PatternItem
                  key={pattern.id}
                  pattern={pattern}
                  onUpdate={(updated) => {
                    const newPatterns = [...lane.patterns];
                    newPatterns[idx] = updated;
                    onUpdate({ ...lane, patterns: newPatterns });
                  }}
                  onDelete={() => {
                    const newPatterns = lane.patterns.filter((_, i) => i !== idx);
                    onUpdate({ ...lane, patterns: newPatterns });
                  }}
                  onAddCard={() => {
                    const newCard: AICard = {
                      id: generateId(),
                      title: '',
                      cardType: 'custom',
                    };
                    const newPatterns = [...lane.patterns];
                    newPatterns[idx] = {
                      ...pattern,
                      cards: [...pattern.cards, newCard],
                    };
                    onUpdate({ ...lane, patterns: newPatterns });
                  }}
                />
              ))}
            </AnimatePresence>

            {/* Add Pattern Button */}
            <button
              onClick={onAddPattern}
              className="flex items-center gap-2 py-2 px-3 w-full text-left text-[12px] text-[#1a73e8] hover:bg-[#e8f0fe] dark:hover:bg-blue-900/30 rounded-lg border border-dashed border-[#1a73e8] transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add pattern
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
LaneItem.displayName = 'LaneItem';

// ============================================================================
// Map Item Component (Level 1)
// ============================================================================
const MapItem = React.forwardRef(function MapItem({ map, onUpdate, onDelete, onAddLane }: MapItemProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div ref={ref} className="border border-[#dadce0] dark:border-gray-600 rounded-xl overflow-hidden">
      {/* Map Header */}
      <div className="group flex items-center gap-3 py-3 px-4 bg-white dark:bg-gray-800 border-b border-[#e8eaed] dark:border-gray-700">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-[#e8eaed] dark:hover:bg-gray-700 rounded"
        >
          <ChevronDownIcon
            className={cn(
              'w-5 h-5 text-[#5f6368] transition-transform',
              !isExpanded && '-rotate-90'
            )}
          />
        </button>

        <EditableName
          value={map.name}
          onChange={(name) => onUpdate({ ...map, name })}
          className="flex-1 text-base text-[#202124] dark:text-gray-100 font-bold"
          placeholder="Enter map name..."
        />

        <span className="text-[12px] text-[#5f6368] dark:text-gray-400">
          {map.lanes.length} lanes
        </span>

        <ActionButtons onDelete={onDelete} />
      </div>

      {/* Lanes */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-3 space-y-3 bg-[#f8f9fa] dark:bg-gray-900"
          >
            <AnimatePresence mode="popLayout">
              {map.lanes.map((lane, idx) => (
                <LaneItem
                  key={lane.id}
                  lane={lane}
                  onUpdate={(updated) => {
                    const newLanes = [...map.lanes];
                    newLanes[idx] = updated;
                    onUpdate({ ...map, lanes: newLanes });
                  }}
                  onDelete={() => {
                    const newLanes = map.lanes.filter((_, i) => i !== idx);
                    onUpdate({ ...map, lanes: newLanes });
                  }}
                  onAddPattern={() => {
                    const newPattern: AIPattern = {
                      id: generateId(),
                      name: '',
                      cards: [],
                    };
                    const newLanes = [...map.lanes];
                    newLanes[idx] = {
                      ...lane,
                      patterns: [...lane.patterns, newPattern],
                    };
                    onUpdate({ ...map, lanes: newLanes });
                  }}
                />
              ))}
            </AnimatePresence>

            {/* Add Lane Button */}
            <button
              onClick={onAddLane}
              className="flex items-center gap-2 py-2.5 px-4 w-full text-left text-[13px] text-[#1a73e8] hover:bg-white dark:hover:bg-gray-800 rounded-lg border border-dashed border-[#1a73e8] transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add lane
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
MapItem.displayName = 'MapItem';

// ============================================================================
// Main Modal Component
// ============================================================================
export function AITrackPreviewModal({
  isOpen,
  onClose,
  initialData,
  onConfirm,
  isCreating = false,
}: AITrackPreviewModalProps) {
  // Internal state for track data
  const [trackData, setTrackData] = useState<AITrackData>(() => ({
    trackName: initialData?.trackName || '',
    trackColor: initialData?.trackColor || '#6366f1',
    trackIcon: initialData?.trackIcon || '🎯',
    trackType: initialData?.trackType || 'hierarchical',
    category: initialData?.category || 'learning',
    description: initialData?.description || '',
    maps: initialData?.maps || [],
    cards: initialData?.cards || [],
  }));

  // Update internal state when initialData changes
  React.useEffect(() => {
    if (initialData) {
      setTrackData({
        trackName: initialData.trackName || '',
        trackColor: initialData.trackColor || '#6366f1',
        trackIcon: initialData.trackIcon || '🎯',
        trackType: initialData.trackType || 'hierarchical',
        category: initialData.category || 'learning',
        description: initialData.description || '',
        maps: initialData.maps || [],
        cards: initialData.cards || [],
      });
    }
  }, [initialData]);

  // Helper to update track data
  const onTrackDataChange = (data: AITrackData) => setTrackData(data);

  // Calculate stats
  const stats = useMemo(() => {
    let totalCards = 0;
    let totalMinutes = 0;

    if (trackData.trackType === 'hierarchical' && trackData.maps) {
      trackData.maps.forEach(map => {
        (map.lanes || []).forEach(lane => {
          (lane.patterns || []).forEach(pattern => {
            const cards = pattern.cards || [];
            totalCards += cards.length;
            cards.forEach(card => {
              totalMinutes += card.estimatedMinutes || 30;
            });
          });
        });
      });
    } else if (trackData.cards) {
      totalCards = trackData.cards.length;
      trackData.cards.forEach(card => {
        totalMinutes += card.estimatedMinutes || 30;
      });
    }

    return { totalCards, totalMinutes };
  }, [trackData]);

  // Add new map
  const handleAddMap = () => {
    const newMap: AIMap = {
      id: generateId(),
      name: '',
      lanes: [],
    };
    onTrackDataChange({
      ...trackData,
      maps: [...(trackData.maps || []), newMap],
    });
  };

  // Add new card (for linear tracks)
  const handleAddCard = () => {
    const newCard: AICard = {
      id: generateId(),
      title: '',
      cardType: 'custom',
    };
    onTrackDataChange({
      ...trackData,
      cards: [...(trackData.cards || []), newCard],
    });
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-[10000]">
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b border-[#e8eaed] dark:border-gray-700 bg-[#f8f9fa] dark:bg-gray-800">
                  <div className="flex items-center gap-3">
                    {/* Track Icon */}
                    {(() => {
                      const IconComponent = getTrackIcon(trackData.trackIcon);
                      return (
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm"
                          style={{ backgroundColor: trackData.trackColor || '#6366f1' }}
                        >
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                      );
                    })()}

                    {/* Track Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <EditableName
                          value={trackData.trackName}
                          onChange={(trackName) => onTrackDataChange({ ...trackData, trackName })}
                          className="font-semibold text-lg text-[#202124] dark:text-gray-100"
                          placeholder="Enter track name..."
                        />
                        {/* AI Badge */}
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white text-[10px] font-medium">
                          <SparklesIcon className="w-3 h-3" />
                          AI
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="flex items-center gap-1 text-[12px] text-[#5f6368] dark:text-gray-400">
                          {trackData.trackType === 'hierarchical' ? (
                            <Layers className="w-3.5 h-3.5" />
                          ) : (
                            <ListChecks className="w-3.5 h-3.5" />
                          )}
                          {trackData.trackType === 'hierarchical' ? 'Hierarchical' : 'Linear'}
                        </span>
                        <span className="text-[12px] text-[#5f6368] dark:text-gray-400">
                          {stats.totalCards} cards
                        </span>
                        {stats.totalMinutes > 0 && (
                          <span className="text-[12px] text-[#1a73e8]">
                            ~{formatTime(stats.totalMinutes)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Close Button */}
                    <button
                      onClick={onClose}
                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5 text-[#5f6368]" />
                    </button>
                  </div>

                  {/* Icon & Color Pickers */}
                  <div className="mt-4 space-y-3">
                    {/* Icon Picker */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                        Icon
                      </label>
                      <div className="flex gap-0.5 overflow-x-auto py-1 hide-scrollbar">
                        {trackIcons.slice(0, 60).map((icon) => {
                          const IC = icon.icon;
                          const isSelected = trackData.trackIcon === icon.id;
                          return (
                            <button
                              key={icon.id}
                              type="button"
                              onClick={() => onTrackDataChange({ ...trackData, trackIcon: icon.id })}
                              className={cn(
                                'p-1.5 rounded-md transition-all flex-shrink-0',
                                isSelected
                                  ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'
                              )}
                              title={icon.label}
                            >
                              <IC className="w-4 h-4" />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Color Picker */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                        Color
                      </label>
                      <div className="flex gap-1.5 overflow-x-auto py-1 hide-scrollbar">
                        {trackColors.map((color) => {
                          const isSelected = trackData.trackColor === color.value;
                          return (
                            <button
                              key={color.id}
                              type="button"
                              onClick={() => onTrackDataChange({ ...trackData, trackColor: color.value })}
                              className={cn(
                                'w-6 h-6 rounded-full flex-shrink-0 transition-all',
                                isSelected && 'ring-2 ring-offset-2 ring-gray-400 dark:ring-offset-gray-900'
                              )}
                              style={{ backgroundColor: color.value }}
                              title={color.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="max-h-[60vh] overflow-y-auto p-4 space-y-3">
                  {/* Hierarchical View */}
                  {trackData.trackType === 'hierarchical' && (
                    <>
                      <AnimatePresence mode="popLayout">
                        {trackData.maps?.map((map, idx) => (
                          <MapItem
                            key={map.id}
                            map={map}
                            onUpdate={(updated) => {
                              const newMaps = [...(trackData.maps || [])];
                              newMaps[idx] = updated;
                              onTrackDataChange({ ...trackData, maps: newMaps });
                            }}
                            onDelete={() => {
                              const newMaps = (trackData.maps || []).filter((_, i) => i !== idx);
                              onTrackDataChange({ ...trackData, maps: newMaps });
                            }}
                            onAddLane={() => {
                              const newLane: AILane = {
                                id: generateId(),
                                name: '',
                                patterns: [],
                              };
                              const newMaps = [...(trackData.maps || [])];
                              newMaps[idx] = {
                                ...map,
                                lanes: [...map.lanes, newLane],
                              };
                              onTrackDataChange({ ...trackData, maps: newMaps });
                            }}
                          />
                        ))}
                      </AnimatePresence>

                      {/* Add Map Button */}
                      <button
                        onClick={handleAddMap}
                        className="flex items-center gap-2 py-3 px-4 w-full text-left text-[14px] text-[#1a73e8] hover:bg-[#e8f0fe] dark:hover:bg-blue-900/30 rounded-xl border-2 border-dashed border-[#1a73e8] transition-colors font-medium"
                      >
                        <PlusIcon className="w-5 h-5" />
                        Add map
                      </button>
                    </>
                  )}

                  {/* Linear View */}
                  {trackData.trackType === 'linear' && (
                    <div className="space-y-1">
                      <AnimatePresence mode="popLayout">
                        {trackData.cards?.map((card, idx) => (
                          <motion.div
                            key={card.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            className="group flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-[#f1f3f4] dark:hover:bg-gray-800 transition-colors border border-[#e8eaed] dark:border-gray-700"
                          >
                            <Checkbox checked={false} />
                            <EditableName
                              value={card.title}
                              onChange={(title) => {
                                const newCards = [...(trackData.cards || [])];
                                newCards[idx] = { ...card, title };
                                onTrackDataChange({ ...trackData, cards: newCards });
                              }}
                              className="flex-1 text-[14px] text-[#202124] dark:text-gray-200 truncate"
                              placeholder="Enter task title..."
                            />
                            <PropertyIcons
                              difficulty={card.difficulty}
                              priority={card.priority}
                              estimatedMinutes={card.estimatedMinutes}
                              onDifficultyChange={(d) => {
                                const newCards = [...(trackData.cards || [])];
                                newCards[idx] = { ...card, difficulty: d };
                                onTrackDataChange({ ...trackData, cards: newCards });
                              }}
                              onPriorityChange={(p) => {
                                const newCards = [...(trackData.cards || [])];
                                newCards[idx] = { ...card, priority: p };
                                onTrackDataChange({ ...trackData, cards: newCards });
                              }}
                              onTimeChange={(m) => {
                                const newCards = [...(trackData.cards || [])];
                                newCards[idx] = { ...card, estimatedMinutes: m };
                                onTrackDataChange({ ...trackData, cards: newCards });
                              }}
                              size="sm"
                            />
                            <ActionButtons
                              onDelete={() => {
                                const newCards = (trackData.cards || []).filter((_, i) => i !== idx);
                                onTrackDataChange({ ...trackData, cards: newCards });
                              }}
                            />
                          </motion.div>
                        ))}
                      </AnimatePresence>

                      {/* Add Card Button */}
                      <button
                        onClick={handleAddCard}
                        className="flex items-center gap-2 py-2.5 px-4 w-full text-left text-[13px] text-[#1a73e8] hover:bg-[#e8f0fe] dark:hover:bg-blue-900/30 rounded-lg border border-dashed border-[#1a73e8] transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add task
                      </button>
                    </div>
                  )}

                  {/* Empty State */}
                  {((trackData.trackType === 'hierarchical' && (!trackData.maps || trackData.maps.length === 0)) ||
                    (trackData.trackType === 'linear' && (!trackData.cards || trackData.cards.length === 0))) && (
                    <div className="py-8 text-center text-[#5f6368] dark:text-gray-400">
                      <p className="text-sm">No items yet</p>
                      <p className="text-xs mt-1">
                        Click the button above to add {trackData.trackType === 'hierarchical' ? 'a map' : 'a task'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-4 border-t border-[#e8eaed] dark:border-gray-700 bg-[#f8f9fa] dark:bg-gray-800">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-[#5f6368] hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={() => onConfirm(trackData)}
                    disabled={isCreating || !trackData.trackName}
                    className={cn(
                      'px-6 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2',
                      isCreating || !trackData.trackName
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-[#1a73e8] text-white hover:bg-[#1557b0]'
                    )}
                  >
                    {isCreating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="w-4 h-4" />
                        Create Track
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default AITrackPreviewModal;
